import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Connect legacy LDAP apps to Monosign with the LDAP Gateway',
    metaDescription:
      'VPNs, NAS boxes, vSphere, and old appliances keep speaking LDAP; the directory behind them becomes Monosign. AD-compatible schema, per-app scoping, and MFA on a protocol that never had it.',
    heroEyebrow: 'How-to · LDAP Gateway',
    heroTitle: ['Your apps speak LDAP.', 'Your directory is Monosign.'],
    heroBody:
      'Plenty of infrastructure still authenticates over LDAP: VPN concentrators, NAS boxes, vSphere, Jira, door systems. The LDAP Gateway presents Monosign to them as an AD-compatible directory (same filters, same attributes) so the app keeps its config and the identity, policy, and audit move to Monosign.',
    badges: ['AD-compatible schema', 'Per-app scoping', 'MFA over LDAP'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the docs',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'From appliance config to governed sign-in.',
    stepsBody: 'Five steps. The legacy app never learns anything changed.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Create the LDAP application in Monosign and assign users',
        body: 'Each connecting system gets its own application with an LDAP key: the key doubles as the service-account bind credential, and the application’s access list decides who may authenticate through it. A user outside the list is refused at bind time, per app.',
        hint: 'This is the audit answer legacy LDAP never had: every appliance is a distinct application with its own user scope and log trail.',
      },
      {
        title: 'Deploy the gateway close to the apps',
        body: 'Run the gateway container on the network segment your LDAP clients live in and point it at your Monosign API. Configuration is environment variables; a health endpoint reports readiness and configuration age for your monitoring.',
      },
      {
        title: 'Point the app at the gateway',
        body: 'Swap the directory host in the appliance for the gateway address. The service account binds with the application key and secret; users resolve under the application’s own branch of the directory tree, so two apps never see each other’s scope.',
        hint: 'AD-style lookups work as-is: sAMAccountName, objectCategory person/group, objectGUID, objectSid, userPrincipalName, memberOf, plus optional RFC 2307 posix attributes for NAS and Linux PAM/SSSD clients.',
      },
      {
        title: 'Turn on MFA, on a protocol that never had it',
        body: 'User binds can require a second factor: append the OTP or method to the password (password,123456 or password,push), or let the gateway fire an automatic push and wait for approval. Timeouts are configurable, and the default is fail-closed.',
        hint: 'Service-account binds complete with the key secret and never enter the MFA path; the classic "MFA broke the bind account" integration trap is structurally impossible.',
      },
      {
        title: 'Verify with ldapsearch, then cut over',
        body: 'Run your app’s real queries against the gateway: bind as the service account, search by sAMAccountName, check memberOf and paged results. When the responses match what the app expects, change the directory address in production; the rollback is changing it back.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Directory', description: 'The user store the gateway publishes over LDAP.', href: '/sign/directory' },
      { label: 'MFA on your VPN', description: 'The RADIUS sibling of this guide.', href: '/learn/mfa-on-vpn' },
      { label: 'Silent Kerberos SSO', description: 'Zero-click sign-in for domain-joined desktops.', href: '/learn/kerberos-sso-setup' },
    ],
  },
  tr: {
    metaTitle: "Legacy LDAP uygulamalarını LDAP Gateway ile Monosign'a bağlayın",
    metaDescription:
      "VPN'ler, NAS kutuları, vSphere ve eski appliance'lar LDAP konuşmaya devam eder; arkalarındaki dizin Monosign olur. AD uyumlu şema, uygulama bazında kapsam ve hiç MFA'sı olmamış bir protokole MFA.",
    heroEyebrow: 'Rehber · LDAP Gateway',
    heroTitle: ['Uygulamalarınız LDAP konuşur.', 'Dizininiz Monosign olur.'],
    heroBody:
      "Altyapının önemli bir kısmı hâlâ LDAP ile kimlik doğrular: VPN yoğunlaştırıcılar, NAS kutuları, vSphere, Jira, geçiş sistemleri. LDAP Gateway, Monosign'ı onlara AD uyumlu bir dizin olarak sunar (aynı filtreler, aynı attribute'lar); uygulama yapılandırmasını korur; kimlik, politika ve denetim Monosign'a taşınır.",
    badges: ['AD uyumlu şema', 'Uygulama bazında kapsam', "LDAP üzerinde MFA"],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Dokümantasyonu açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: "Appliance ayarından yönetilen girişe.",
    stepsBody: 'Beş adım. Legacy uygulama bir şeyin değiştiğini hiç öğrenmez.',
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: "Monosign'da LDAP uygulamasını oluşturun ve kullanıcıları atayın",
        body: 'Bağlanan her sistem, LDAP anahtarlı kendi uygulamasını alır: anahtar aynı zamanda servis hesabı bind kimliğidir ve uygulamanın erişim listesi, üzerinden kimlerin doğrulanabileceğine karar verir. Listede olmayan kullanıcı bind anında, uygulama bazında reddedilir.',
        hint: "Bu, legacy LDAP'ın hiç veremediği denetim yanıtıdır: her appliance, kendi kullanıcı kapsamı ve log izine sahip ayrı bir uygulamadır.",
      },
      {
        title: "Gateway'i uygulamalara yakın konumlandırın",
        body: "Gateway konteynerini LDAP istemcilerinizin yaşadığı ağ segmentinde çalıştırın ve Monosign API'nize yönlendirin. Yapılandırma ortam değişkenleriyledir; health endpoint'i izleme sisteminize hazır olma durumunu ve yapılandırma yaşını raporlar.",
      },
      {
        title: "Uygulamayı gateway'e yönlendirin",
        body: "Appliance'taki dizin adresini gateway adresiyle değiştirin. Servis hesabı, uygulama anahtarı ve secret ile bind olur; kullanıcılar dizin ağacının uygulamaya ait dalında çözülür; iki uygulama birbirinin kapsamını asla görmez.",
        hint: "AD tarzı sorgular olduğu gibi çalışır: sAMAccountName, objectCategory person/group, objectGUID, objectSid, userPrincipalName, memberOf; ayrıca NAS ve Linux PAM/SSSD istemcileri için isteğe bağlı RFC 2307 posix attribute'ları.",
      },
      {
        title: "MFA'yı açın, hiç MFA'sı olmamış bir protokolde",
        body: "Kullanıcı bind'leri ikinci faktör isteyebilir: OTP'yi ya da yöntemi parolanın sonuna ekleyin (parola,123456 veya parola,push) ya da gateway otomatik push gönderip onayı beklesin. Süreler yapılandırılabilir; varsayılan fail-closed'dur.",
        hint: 'Servis hesabı bind\'leri anahtar secret\'ıyla tamamlanır ve MFA yoluna hiç girmez; klasik "MFA bind hesabını kırdı" entegrasyon tuzağı yapısal olarak imkânsızdır.',
      },
      {
        title: "ldapsearch ile doğrulayın, sonra geçin",
        body: "Uygulamanızın gerçek sorgularını gateway'e karşı çalıştırın: servis hesabıyla bind olun, sAMAccountName ile arayın, memberOf ve sayfalı sonuçları kontrol edin. Yanıtlar uygulamanın beklediğiyle eşleşince üretimde dizin adresini değiştirin; geri dönüş, adresi geri almaktan ibarettir.",
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Dizin', description: "Gateway'in LDAP üzerinden yayımladığı kullanıcı deposu.", href: '/sign/directory' },
      { label: "VPN'inizde MFA", description: 'Bu rehberin RADIUS kardeşi.', href: '/learn/mfa-on-vpn' },
      { label: 'Sessiz Kerberos SSO', description: "Domain'e bağlı masaüstlerine sıfır tık giriş.", href: '/learn/kerberos-sso-setup' },
    ],
  },
}

type Props = { locale: string }

export default function LdapGatewayGuidePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/ldap-gateway-legacy-apps"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
